/**
 * التأمينات الاجتماعية وضريبة الدخل.
 *
 * ## الأرقامُ بياناتٌ لا شيفرة
 *
 * الشرائحُ والنِّسبُ والحدودُ كلُّها في إعدادات الشركة، والبذرةُ هنا نقطةُ
 * بدايةٍ تُراجَع لا حكمٌ يُطبَّق. والقوانينُ تتغيّر بقرارٍ في الجريدة
 * الرسمية، ونظامٌ يحبس نسبةً في شيفرته يجعل تعديلَ قانونٍ إصدارَ برنامج.
 *
 * والشاشةُ تقول ذلك صراحةً: ما يُحسب هنا يُراجَع من مكتبٍ محاسبيٍّ قبل أن
 * يُبنى عليه إقرار.
 *
 * ## والترتيبُ يغيّر الرقم
 *
 * التأمينُ يُخصم قبل حساب الضريبة لا بعدها، والإعفاءُ الشخصيُّ يُطرح من
 * الوعاء لا من الضريبة. وعكسُ أيٍّ من هذين يُنتج رقمًا قريبًا يبدو صحيحًا
 * ويختلف بمئاتٍ في السنة.
 */

/* ==========================================================================
   التأمينات
   ========================================================================== */

export interface InsuranceRules {
  /** حصّةُ الموظّف من الأجر التأميني، نسبةً مئوية. */
  employeeRate: number;
  /** حصّةُ صاحب العمل — تُسجَّل كتكلفةٍ ولا تُخصم من الموظّف. */
  employerRate: number;
  /** الحدُّ الأدنى للأجر التأميني شهريًّا. */
  wageMin: number;
  /** الحدُّ الأقصى للأجر التأميني شهريًّا. */
  wageMax: number;
}

export interface InsuranceResult {
  /** الأجرُ التأمينيّ بعد الحدّين. */
  wage: number;
  employeeShare: number;
  employerShare: number;
  /** هل حُدّ الأجرُ بأحد الطرفين — يُقال في القسيمة. */
  clamped: 'MIN' | 'MAX' | null;
}

/**
 * حصّةُ الموظّف وصاحب العمل.
 *
 * الأجرُ التأمينيُّ غيرُ الراتب الأساسيّ: له حدٌّ أدنى وأعلى بالقانون،
 * وشركاتٌ تؤمّن على أقلّ من الراتب. فمن ترك الأجر التأمينيّ فارغًا يُؤمَّن
 * على أساسيّه، ومن كتبه يُؤمَّن عليه — وكلاهما يُحدّ بالحدّين.
 */
export function computeInsurance(
  insuranceWage: number | null,
  baseSalary: number,
  rules: InsuranceRules,
): InsuranceResult {
  const raw = insuranceWage ?? baseSalary;

  let wage = raw;
  let clamped: 'MIN' | 'MAX' | null = null;
  if (rules.wageMin > 0 && wage < rules.wageMin) {
    wage = rules.wageMin;
    clamped = 'MIN';
  }
  if (rules.wageMax > 0 && wage > rules.wageMax) {
    wage = rules.wageMax;
    clamped = 'MAX';
  }

  return {
    wage: round2(wage),
    employeeShare: round2((wage * rules.employeeRate) / 100),
    employerShare: round2((wage * rules.employerRate) / 100),
    clamped,
  };
}

/* ==========================================================================
   ضريبة الدخل
   ========================================================================== */

export interface TaxBracket {
  /**
   * حدُّ الشريحة السنويُّ الأعلى — و`null` للشريحة الأخيرة المفتوحة.
   *
   * والشرائحُ تصاعديّة: كلُّ شريحةٍ تُطبَّق على ما بينها وبين سابقتها لا
   * على الوعاء كلِّه. وحسابُها على الكلّ يُنتج قفزةً عند حدّ الشريحة —
   * جنيهٌ زائدٌ في الدخل يأكل آلافًا من الصافي.
   */
  upTo: number | null;
  rate: number;
}

export interface TaxRules {
  brackets: readonly TaxBracket[];
  /** الإعفاءُ الشخصيُّ السنويّ — يُطرح من الوعاء لا من الضريبة. */
  personalExemption: number;
  /** ما يكتبه المحاسبُ ليعرف من بعده أيَّ قانونٍ أدخل. */
  note: string;
}

export interface TaxResult {
  /** الوعاءُ السنويُّ بعد التأمين والإعفاء. */
  annualBase: number;
  annualTax: number;
  monthlyTax: number;
  /** ما دُفع في كلّ شريحة — يُعرض لمن يريد أن يفهم الرقم. */
  slices: { from: number; to: number | null; rate: number; tax: number }[];
}

/**
 * ضريبةُ الشهر من دخلٍ شهريّ.
 *
 * تُحسب سنويًّا ثمّ تُقسَّم على اثني عشر: الشرائحُ سنويّةٌ في القانون،
 * وتطبيقُها على دخلٍ شهريٍّ مباشرةً يضع من راتبُه عشرةُ آلافٍ في شريحةٍ
 * أعلى بكثيرٍ ممّا يستحقّ.
 *
 * وهذا يفترض أنّ الشهرَ يمثّل السنة — وهو تقريبٌ صحيحٌ لراتبٍ ثابت،
 * ويخطئ في شهرٍ فيه مكافأةٌ كبيرة. والتسويةُ السنويّةُ عملُ المحاسب لا
 * عملُ هذا الحساب، والشاشةُ تقول ذلك.
 */
export function computeTax(
  monthlyTaxable: number,
  rules: TaxRules,
): TaxResult {
  const annualGross = monthlyTaxable * 12;
  const annualBase = Math.max(0, annualGross - rules.personalExemption);

  const slices: TaxResult['slices'] = [];
  let annualTax = 0;
  let floor = 0;

  for (const bracket of rules.brackets) {
    const ceiling = bracket.upTo ?? Number.POSITIVE_INFINITY;
    if (annualBase <= floor) break;

    const inSlice = Math.min(annualBase, ceiling) - floor;
    if (inSlice > 0) {
      const tax = (inSlice * bracket.rate) / 100;
      annualTax += tax;
      slices.push({
        from: floor,
        to: bracket.upTo,
        rate: bracket.rate,
        tax: round2(tax),
      });
    }

    floor = ceiling;
    if (!Number.isFinite(floor)) break;
  }

  return {
    annualBase: round2(annualBase),
    annualTax: round2(annualTax),
    monthlyTax: round2(annualTax / 12),
    slices,
  };
}

/* ==========================================================================
   البذور
   ========================================================================== */

/**
 * نقطةُ بدايةٍ مصريّةٍ تُراجَع.
 *
 * أرقامٌ منشورةٌ وقتَ الكتابة، وليست فتوى: القوانينُ تتغيّر، وما يُبنى
 * عليه إقرارٌ ضريبيٌّ يُراجَع من مكتبٍ محاسبيّ. والشاشةُ تحمل حقلَ
 * «الملاحظة» ليكتب من أدخلها أيَّ قانونٍ أدخل ومتى.
 */
export const DEFAULT_INSURANCE_RULES: InsuranceRules = {
  employeeRate: 11,
  employerRate: 18.75,
  wageMin: 2_000,
  wageMax: 14_500,
};

export const DEFAULT_TAX_BRACKETS: readonly TaxBracket[] = [
  { upTo: 40_000, rate: 0 },
  { upTo: 55_000, rate: 10 },
  { upTo: 70_000, rate: 15 },
  { upTo: 200_000, rate: 20 },
  { upTo: 400_000, rate: 22.5 },
  { upTo: 1_200_000, rate: 25 },
  { upTo: null, rate: 27.5 },
] as const;

export const DEFAULT_PERSONAL_EXEMPTION = 20_000;

/**
 * تنبيهٌ يُعرض حيثما ظهر رقمُ ضريبةٍ أو تأمين.
 *
 * لا يُخفى ولا يُصغَّر: من يقرأ رقمًا في شاشةٍ يفترض أنّه صحيحٌ قانونًا،
 * وهذا النظامُ يحسب بما أُدخل فيه لا بما في الجريدة الرسمية.
 */
export const TAX_DISCLAIMER =
  'الشرائح والنِّسب مُدخَلةٌ في الإعدادات وتُراجَع من محاسبك — النظام يحسب بما أُدخل فيه لا بما في القانون.';

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
